'use client'

import { m } from 'framer-motion'
import { CheckCircle2, Lightbulb, Target, Trophy } from 'lucide-react'

export type CaseObjectiveCard = {
  kind?: 'task' | 'solution' | 'result'
  label?: string
  title: string
  desc: string
}

export type CaseObjectivesBlock = {
  cards?: CaseObjectiveCard[]
  summary?: {
    label?: string
    title?: string
    highlight?: string
    paragraphs?: string[]
  }
}

function iconAndColor(kind: CaseObjectiveCard['kind']) {
  switch (kind) {
    case 'solution':
      return { icon: Lightbulb, color: 'text-purple-300' }
    case 'result':
      return { icon: Trophy, color: 'text-emerald-300' }
    case 'task':
    default:
      return { icon: Target, color: 'text-blue-300' }
  }
}

export default function CaseObjectives({
  objectives,
}: {
  objectives?: CaseObjectivesBlock | null
}) {
  if (!objectives) return null

  const cards = (objectives.cards || []).slice(0, 3)
  const summary = objectives.summary

  return (
    <section className="home-section relative z-20 bg-transparent">
      <div className="home-container">
        {cards.length > 0 && (
          <div className="mb-10 grid grid-cols-1 gap-6 sm:mb-12 sm:grid-cols-2 sm:gap-8 xl:grid-cols-3">
            {cards.map((item, i) => {
              const { icon: Icon, color } = iconAndColor(item.kind)
              const label =
                item.label ??
                (item.kind === 'solution'
                  ? 'Решение'
                  : item.kind === 'result'
                    ? 'Результат'
                    : 'Задача')

              return (
                <m.div
                  key={`obj-card-${i}`}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="group relative overflow-hidden rounded-[28px] border border-white/10 bg-white/5 p-6 backdrop-blur-xl transition-colors hover:bg-white/10 sm:rounded-[40px] sm:p-8"
                >
                  <div className="pointer-events-none absolute top-0 right-0 -mt-16 -mr-16 h-32 w-32 rounded-full bg-white/5 blur-3xl transition-colors group-hover:bg-white/10" />

                  <div className={`home-meta mb-6 flex items-center gap-2 uppercase ${color}`}>
                    <Icon size={16} /> {label}
                  </div>
                  <h3 className="home-card-title-sm mb-3 leading-tight break-words hyphens-auto text-white sm:mb-4">
                    {item.title}
                  </h3>
                  <p className="home-subnote leading-relaxed break-words hyphens-auto text-gray-400">
                    {item.desc}
                  </p>
                </m.div>
              )
            })}
          </div>
        )}

        {summary && (summary.title || (summary.paragraphs && summary.paragraphs.length > 0)) && (
          <m.div
            initial={{ opacity: 0, scale: 0.97 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="relative overflow-hidden rounded-[32px] border border-white/10 bg-white/[0.03] p-7 backdrop-blur-md sm:rounded-[48px] sm:p-10 md:p-16"
          >
            <div className="relative z-10 grid grid-cols-1 gap-10 sm:gap-12 md:grid-cols-2">
              <div>
                <div className="home-meta mb-6 flex items-center gap-2 text-blue-300 uppercase">
                  <CheckCircle2 size={16} /> {summary.label ?? 'Что сделали'}
                </div>

                <h2 className="home-title leading-tight text-white">
                  {summary.title ?? ''}
                  {summary.highlight ? (
                    <>
                      <br />
                      <span className="bg-gradient-to-r from-blue-300 to-purple-300 bg-clip-text text-transparent">
                        {summary.highlight}
                      </span>
                    </>
                  ) : null}
                </h2>
              </div>

              {summary.paragraphs && summary.paragraphs.length > 0 && (
                <div className="home-subnote space-y-5 leading-relaxed text-gray-300 sm:space-y-6">
                  {summary.paragraphs.map((p, i) => (
                    <p key={`sum-${i}`}>{p}</p>
                  ))}
                </div>
              )}
            </div>
          </m.div>
        )}
      </div>
    </section>
  )
}
